import { useState, useMemo } from "react";
import { useRegisterHeaderAction } from "@/hooks/useRegisterHeaderAction";
import { useAuth } from "@/contexts/AuthContext";
import PartnerDetailSheet from "@/components/PartnerDetailSheet";
import { useProveedores, useCreateProveedor, useDeleteProveedor } from "@/hooks/useProveedores";
import { useClientes, useCreateCliente, useDeleteCliente } from "@/hooks/useClientes";
import { MergeProviderModal } from "@/components/MergeProviderModal";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Building2, Users, Plus, Trash2, PackageOpen, ArrowLeftRight, BrainCircuit, Sparkles } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAiAliases, useDeleteAiAlias, type AiAlias } from "@/hooks/useAiAliases";

interface EntityFormValues {
  nombre: string;
  nit: string;
  pais: string;
}

const INITIAL_FORM: EntityFormValues = { nombre: "", nit: "", pais: "" };

function EmptyState({ label }: { label: string }) {
  return (
    <TableRow>
      <TableCell colSpan={5} className="h-40">
        <div className="flex flex-col items-center justify-center gap-2 text-muted-foreground">
          <PackageOpen className="h-10 w-10 opacity-40" />
          <p className="text-sm font-medium">Sin {label} registrados</p>
          <p className="text-xs">Añade el primero usando el botón superior.</p>
        </div>
      </TableCell>
    </TableRow>
  );
}

function CreateEntityModal({
  open,
  onOpenChange,
  title,
  onSubmit,
  isPending,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  title: string;
  onSubmit: (values: EntityFormValues) => void;
  isPending: boolean;
}) {
  const [form, setForm] = useState<EntityFormValues>(INITIAL_FORM);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.nombre.trim()) return;
    onSubmit(form);
    setForm(INITIAL_FORM);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="nombre">Razón Social *</Label>
            <Input
              id="nombre"
              value={form.nombre}
              onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))}
              placeholder="Nombre de la empresa"
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="nit">NIT / Tax ID</Label>
            <Input
              id="nit"
              value={form.nit}
              onChange={(e) => setForm((f) => ({ ...f, nit: e.target.value }))}
              placeholder="Ej: 900.123.456-7"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="pais">País</Label>
            <Input
              id="pais"
              value={form.pais}
              onChange={(e) => setForm((f) => ({ ...f, pais: e.target.value }))}
              placeholder="Ej: Colombia"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isPending || !form.nombre.trim()}>
              {isPending ? "Guardando..." : "Guardar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Directorio() {
  const { userRole } = useAuth();
  const { toast } = useToast();
  const canEdit = userRole !== "VISOR";

  const { data: proveedores = [], isLoading: loadingProv } = useProveedores();
  const { data: clientes = [], isLoading: loadingCli } = useClientes();
  const createProveedor = useCreateProveedor();
  const createCliente = useCreateCliente();
  const deleteProveedor = useDeleteProveedor();
  const deleteCliente = useDeleteCliente();

  const [provModal, setProvModal] = useState(false);
  const [cliModal, setCliModal] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState<{ data: any; type: "proveedor" | "cliente" } | null>(null);
  const [selectedProvIds, setSelectedProvIds] = useState<Set<string>>(new Set());
  const [mergeOpen, setMergeOpen] = useState(false);

  const { data: aliases = [], isLoading: loadingAliases } = useAiAliases();
  const deleteAlias = useDeleteAiAlias();
  const [aliasToDelete, setAliasToDelete] = useState<AiAlias | null>(null);

  const headerAction = useMemo(
    () =>
      canEdit ? (
        <Button variant="outline" onClick={() => setProvModal(true)} className="gap-2 border-primary/30 text-primary hover:bg-primary/10">
          <Plus className="h-4 w-4" />
          Añadir Proveedor
        </Button>
      ) : null,
    [canEdit]
  );
  useRegisterHeaderAction(headerAction);

  const confirmDeleteAlias = () => {
    if (!aliasToDelete) return;
    deleteAlias.mutate(
      { id: aliasToDelete.id, type: aliasToDelete.type },
      {
        onSuccess: () => {
          toast({ title: "Alias eliminado", description: "La IA dejará de usar esta vinculación." });
          setAliasToDelete(null);
        },
        onError: (err) =>
          toast({ title: "Error", description: (err as Error).message, variant: "destructive" }),
      }
    );
  };

  const toggleProvSelection = (id: string) => {
    setSelectedProvIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        if (next.size >= 2) return prev; // max 2
        next.add(id);
      }
      return next;
    });
  };

  const selectedProvs = proveedores.filter((p) => selectedProvIds.has(p.id));

  const handleCreateProv = (values: EntityFormValues) => {
    createProveedor.mutate(values, {
      onSuccess: () => {
        setProvModal(false);
        toast({ title: "✅ Proveedor creado" });
      },
      onError: (err) => toast({ title: "Error", description: (err as Error).message, variant: "destructive" }),
    });
  };

  const handleCreateCli = (values: EntityFormValues) => {
    createCliente.mutate(values, {
      onSuccess: () => {
        setCliModal(false);
        toast({ title: "✅ Cliente creado" });
      },
      onError: (err) => toast({ title: "Error", description: (err as Error).message, variant: "destructive" }),
    });
  };

  const handleDeleteProv = (id: string) => {
    deleteProveedor.mutate(id, {
      onSuccess: () => toast({ title: "Proveedor eliminado" }),
      onError: (err) => toast({ title: "Error", description: (err as Error).message, variant: "destructive" }),
    });
  };

  const handleDeleteCli = (id: string) => {
    deleteCliente.mutate(id, {
      onSuccess: () => toast({ title: "Cliente eliminado" }),
      onError: (err) => toast({ title: "Error", description: (err as Error).message, variant: "destructive" }),
    });
  };

  const thClass = "text-xs text-muted-foreground font-semibold";

  const renderProveedoresTable = () => (
    <div className="rounded-lg border border-border bg-card">
      <Table>
        <TableHeader>
          <TableRow>
            {canEdit && <TableHead className={`${thClass} w-[40px]`}></TableHead>}
            <TableHead className={thClass}>Razón Social</TableHead>
            <TableHead className={thClass}>NIT / Tax ID</TableHead>
            <TableHead className={thClass}>País</TableHead>
            <TableHead className={`${thClass} text-center`}>Embarques</TableHead>
            <TableHead className={thClass}>Fecha de Registro</TableHead>
            {canEdit && <TableHead className={`${thClass} w-[60px]`}></TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {loadingProv ? (
            <TableRow>
              <TableCell colSpan={canEdit ? 6 : 4} className="text-center text-muted-foreground py-8">
                Cargando...
              </TableCell>
            </TableRow>
          ) : proveedores.length === 0 ? (
            <EmptyState label="proveedores" />
          ) : (
            proveedores.map((item) => (
              <TableRow
                key={item.id}
                className={`cursor-pointer hover:bg-muted/50 ${selectedProvIds.has(item.id) ? "bg-primary/5" : ""}`}
                onClick={() => setSelectedPartner({ data: item, type: "proveedor" })}
              >
                {canEdit && (
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selectedProvIds.has(item.id)}
                      disabled={!selectedProvIds.has(item.id) && selectedProvIds.size >= 2}
                      onCheckedChange={() => toggleProvSelection(item.id)}
                    />
                  </TableCell>
                )}
                <TableCell className="font-medium text-foreground">{item.nombre}</TableCell>
                <TableCell className="text-sm font-mono text-muted-foreground">{item.nit || "—"}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{item.pais || "—"}</TableCell>
                <TableCell className="text-center">
                  <Badge variant="secondary" className="text-[11px] h-5 min-w-[28px] px-1.5 justify-center">
                    {(item as any).embarques?.[0]?.count ?? 0}
                  </Badge>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {new Date(item.created_at).toLocaleDateString("es-CO", { day: "2-digit", month: "short", year: "numeric" })}
                </TableCell>
                {canEdit && (
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-destructive"
                      onClick={(e) => { e.stopPropagation(); handleDeleteProv(item.id); }}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );

  const renderClientesTable = () => (
    <div className="rounded-lg border border-border bg-card">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className={thClass}>Razón Social</TableHead>
            <TableHead className={thClass}>NIT / Tax ID</TableHead>
            <TableHead className={thClass}>País</TableHead>
            <TableHead className={thClass}>Fecha de Registro</TableHead>
            {canEdit && <TableHead className={`${thClass} w-[60px]`}></TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {loadingCli ? (
            <TableRow>
              <TableCell colSpan={canEdit ? 5 : 4} className="text-center text-muted-foreground py-8">
                Cargando...
              </TableCell>
            </TableRow>
          ) : clientes.length === 0 ? (
            <EmptyState label="clientes" />
          ) : (
            clientes.map((item) => (
              <TableRow key={item.id} className="cursor-pointer hover:bg-muted/50" onClick={() => setSelectedPartner({ data: item, type: "cliente" })}>
                <TableCell className="font-medium text-foreground">{item.nombre}</TableCell>
                <TableCell className="text-sm font-mono text-muted-foreground">{item.nit || "—"}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{item.pais || "—"}</TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {new Date(item.created_at).toLocaleDateString("es-CO", { day: "2-digit", month: "short", year: "numeric" })}
                </TableCell>
                {canEdit && (
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-destructive"
                      onClick={(e) => { e.stopPropagation(); handleDeleteCli(item.id); }}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );

  return (
    <main className="flex-1 p-6 space-y-6 overflow-auto">
      <Tabs defaultValue="proveedores" className="space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Building2 className="h-6 w-6 text-primary" />
            <h2 className="text-lg font-semibold text-foreground">Datos Maestros</h2>
          </div>
          <TabsList>
            <TabsTrigger value="proveedores" className="gap-1.5">
              <Building2 className="h-3.5 w-3.5" />
              Proveedores
              <Badge variant="secondary" className="ml-1 text-[10px] h-5 px-1.5">
                {proveedores.length}
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="clientes" className="gap-1.5">
              <Users className="h-3.5 w-3.5" />
              Clientes
              <Badge variant="secondary" className="ml-1 text-[10px] h-5 px-1.5">
                {clientes.length}
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="aliases" className="gap-1.5">
              <BrainCircuit className="h-3.5 w-3.5" />
              Memoria de IA (Alias)
              <Badge variant="secondary" className="ml-1 text-[10px] h-5 px-1.5">
                {aliases.length}
              </Badge>
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="proveedores" className="space-y-4">
          {canEdit && (
            <div className="flex items-center justify-between gap-3">
              {selectedProvIds.size === 2 ? (
                <Button
                  variant="outline"
                  className="gap-2 border-primary text-primary"
                  onClick={() => setMergeOpen(true)}
                >
                  <ArrowLeftRight className="h-4 w-4" />
                  Unificar Proveedores ({selectedProvIds.size}/2)
                </Button>
              ) : (
                <p className="text-xs text-muted-foreground">
                  {selectedProvIds.size === 1
                    ? "Selecciona 1 proveedor más para unificar"
                    : "Selecciona 2 proveedores para unificar"}
                </p>
              )}
              <Button className="gap-2" onClick={() => setProvModal(true)}>
                <Plus className="h-4 w-4" />
                Añadir Proveedor
              </Button>
            </div>
          )}
          {renderProveedoresTable()}
        </TabsContent>

        <TabsContent value="clientes" className="space-y-4">
          {canEdit && (
            <div className="flex justify-end">
              <Button className="gap-2" onClick={() => setCliModal(true)}>
                <Plus className="h-4 w-4" />
                Añadir Cliente
              </Button>
            </div>
          )}
          {renderClientesTable()}
        </TabsContent>

        <TabsContent value="aliases" className="space-y-4">
          <div className="flex items-start gap-3 rounded-lg border border-primary/20 bg-primary/5 p-3">
            <Sparkles className="h-4 w-4 text-primary mt-0.5 shrink-0" />
            <div className="text-xs text-muted-foreground leading-relaxed">
              Cada vez que un operador vincula manualmente un nombre extraído por OCR a un Cliente o Proveedor del directorio,
              la IA lo memoriza aquí para auto-vincularlo en futuros documentos. Elimina los alias incorrectos para corregir la memoria del sistema.
            </div>
          </div>
          <div className="rounded-lg border border-border bg-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className={thClass}>Texto Leído (OCR)</TableHead>
                  <TableHead className={thClass}>Entidad Oficial</TableHead>
                  <TableHead className={`${thClass} w-[110px]`}>Tipo</TableHead>
                  <TableHead className={`${thClass} w-[160px]`}>Aprendido</TableHead>
                  {canEdit && <TableHead className={`${thClass} w-[60px]`}></TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {loadingAliases ? (
                  Array.from({ length: 4 }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell><Skeleton className="h-4 w-3/4" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-2/3" /></TableCell>
                      <TableCell><Skeleton className="h-5 w-16 rounded-full" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                      {canEdit && <TableCell><Skeleton className="h-7 w-7" /></TableCell>}
                    </TableRow>
                  ))
                ) : aliases.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={canEdit ? 5 : 4} className="h-40">
                      <div className="flex flex-col items-center justify-center gap-2 text-muted-foreground">
                        <BrainCircuit className="h-10 w-10 opacity-40" />
                        <p className="text-sm font-medium">Aún no hay alias aprendidos</p>
                        <p className="text-xs">La IA empezará a memorizar vinculaciones a medida que asignes clientes y proveedores.</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  aliases.map((alias) => (
                    <TableRow key={`${alias.type}-${alias.id}`}>
                      <TableCell className="font-mono text-[13px] text-foreground">{alias.ocr_text}</TableCell>
                      <TableCell className="font-medium text-foreground">{alias.official_name}</TableCell>
                      <TableCell>
                        {alias.type === "cliente" ? (
                          <Badge variant="secondary" className="gap-1 text-[10px] h-5 px-1.5 bg-primary/10 text-primary border border-primary/20">
                            <Users className="h-3 w-3" />
                            Cliente
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="gap-1 text-[10px] h-5 px-1.5 bg-secondary text-secondary-foreground border border-border">
                            <Building2 className="h-3 w-3" />
                            Proveedor
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {new Date(alias.created_at).toLocaleDateString("es-CO", { day: "2-digit", month: "short", year: "numeric" })}
                      </TableCell>
                      {canEdit && (
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-muted-foreground hover:text-destructive"
                            onClick={() => setAliasToDelete(alias)}
                            aria-label="Eliminar alias"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      <AlertDialog open={!!aliasToDelete} onOpenChange={(v) => !v && setAliasToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar este alias?</AlertDialogTitle>
            <AlertDialogDescription>
              La IA dejará de vincular automáticamente el texto{" "}
              <span className="font-mono font-medium text-foreground">"{aliasToDelete?.ocr_text}"</span>{" "}
              con{" "}
              <span className="font-medium text-foreground">{aliasToDelete?.official_name}</span>.
              Esta acción no afecta al {aliasToDelete?.type} en el directorio.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteAlias.isPending}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmDeleteAlias(); }}
              disabled={deleteAlias.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteAlias.isPending ? "Eliminando..." : "Eliminar alias"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <CreateEntityModal
        open={provModal}
        onOpenChange={setProvModal}
        title="Nuevo Proveedor"
        onSubmit={handleCreateProv}
        isPending={createProveedor.isPending}
      />
      <CreateEntityModal
        open={cliModal}
        onOpenChange={setCliModal}
        title="Nuevo Cliente"
        onSubmit={handleCreateCli}
        isPending={createCliente.isPending}
      />
      <PartnerDetailSheet
        open={!!selectedPartner}
        onOpenChange={(v) => !v && setSelectedPartner(null)}
        partner={selectedPartner?.data ?? null}
        type={selectedPartner?.type ?? "proveedor"}
      />
      {selectedProvs.length === 2 && (
        <MergeProviderModal
          open={mergeOpen}
          onOpenChange={setMergeOpen}
          proveedorA={selectedProvs[0]}
          proveedorB={selectedProvs[1]}
          onMerged={() => setSelectedProvIds(new Set())}
        />
      )}
    </main>
  );
}